/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useState, useEffect } from 'react';
import Grid from '@material-ui/core/Grid';
import Modal from '@material-ui/core/Modal';
import withStyles from '@material-ui/core/styles/withStyles';
import {
    object, bool, func, string, shape, arrayOf, number,
} from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PersonalizeCard from './PersonalizeView/PersonalizeCard';
import ReviewCard from './ReviewCard/ReviewCard';
import GraphqlOccasionDataContainer from './SelectTemplateView/GraphqlOccasionDataContainer';
import { fetchGCIKey } from '../../../../state/ducks/App/App-Actions';
import { addCGCDataToApi } from '../../../../state/ducks/CGC/CGC-Actions';
import { getCGCCardData, getDataDictionary } from '../../../../state/ducks/App/App-Selectors';
import CGCModalHeader from './CGCModalHeader';
import CelebrationCardTopView from './CelebrationCardTopView';

const styles = (theme) => ({
    container: {
        position: 'absolute',
        top: 50,
        left: '50%',
        transform: 'translateX(-50%)',
        maxWidth: 1280,
        width: '80%',
        height: '90vh',
        borderRadius: 5,
        border: `1px solid ${theme.palette.cgc?.borderColor || '#666'}`,
        background: theme.palette.cgc?.bgColor || 'white',
        '&:focus': {
            outline: 'none',
        },
        [theme.breakpoints.down('sm')]: {
            minWidth: '100%',
            height: '100%',
            top: 0,
        },
    },
});

const MODAL_VIEWS = {
    SELECT_TEMPLATE: 'select',
    PERSONALIZE: 'personalize',
    REVIEW: 'review',
};

const CGCModal = ({
    classes,
    openModal,
    closeModal,
    occasionData,
    orderItemsId,
    productSku,
    brandData,
    // brandUid is the UID of the brand in contentstack - Helps query the data from contentstack
    brandUid,
    brand,
    addCGCData,
    editCard,
    getCGCKey,
    dataDictionary,
}) => {
    // Edit card -- if the Modal is edit view
    // gets the data from state and checks if the data exist
    const { cgcCard, orderItemId, cgcMessage } = editCard;
    // Celebrations Modal State - The data is being stored in the state and will be updated to redux after clicking on Add To Cart
    const [modalView, setModalView] = useState(cgcCard ? MODAL_VIEWS.PERSONALIZE : MODAL_VIEWS.SELECT_TEMPLATE);
    const [selectedOccasion, setOccasion] = useState('');
    const [selectedCard, setCardTemplate] = useState({});
    const [messageData, setMessageData] = useState({});
    const [isEditMode, setEditMode] = useState(false);

    const resetState = () => {
        setCardTemplate({});
        setMessageData({});
        setOccasion('');
    };
    useEffect(() => {
        resetState();
        // get PersonalizationKey from API and store in redux
        const params = {
            orderItemId: orderItemsId,
            brandCode: brand.id,
            brand: brand.domain,
        };
        getCGCKey(params);
        // set the state to the selected data if view is edit card
        if (orderItemId) {
            setCardTemplate(cgcCard);
            setMessageData(cgcMessage);
            setEditMode(true);
        }
    }, [orderItemsId]);

    // onChange Handler for setting Greeting Card in state: Greeting Card Object
    const onSelectTemplate = (template) => {
        setCardTemplate(template);
        setModalView(MODAL_VIEWS.PERSONALIZE);
    };

    // onChange Handler for setting message in state: message Object, consists of => attributes(styles) and text
    const setCardMessage = (data) => {
        setMessageData(data);
    };

    // Add To Cart Function. Calls the saga operations and adds the card to the redux state and API
    const addToCart = () => {
        const cgcData = {
            orderItemId: orderItemsId, cgcCard: selectedCard, cgcMessage: messageData, productSku, brandData,
        };
        addCGCData({
            cardData: selectedCard, message: messageData, cgcData,
        });
        closeModal(false);
    };

    return (
        <Modal open={openModal}>
            <Grid className={classes.container}>
                <CGCModalHeader
                    closeModal={closeModal}
                />
                <CelebrationCardTopView
                    occasionData={occasionData}
                    selectedOccasion={selectedOccasion}
                    change={setOccasion}
                    setModalView={setModalView}
                    modalViews={MODAL_VIEWS}
                    selectedView={modalView}
                    selectedCard={selectedCard}
                    messageData={messageData}
                    dataDictionary={dataDictionary}
                />
                {/* Renders the View based on the selected view in the state */}
                {modalView === MODAL_VIEWS.SELECT_TEMPLATE
                    ? (
                        <GraphqlOccasionDataContainer
                            occasions={occasionData}
                            brandUid={brandUid}
                            selectedOccasion={selectedOccasion}
                            setCardTemplate={onSelectTemplate}
                        />
                    )
                    : null}
                {modalView === MODAL_VIEWS.PERSONALIZE
                    ?             (
                        <PersonalizeCard
                            isEditMode={isEditMode}
                            setCardMessage={setCardMessage}
                            messageData={messageData}
                            selectedCard={selectedCard}
                            onSave={() => setModalView(MODAL_VIEWS.REVIEW)}
                            dataDictionary={dataDictionary}
                        />
                    )
                    : null}
                {modalView === MODAL_VIEWS.REVIEW
                    ?             (
                        <ReviewCard
                            cardData={selectedCard}
                            messageData={messageData}
                            addToCart={addToCart}
                            dataDictionary={dataDictionary}
                        />
                    )
                    : null}
            </Grid>
        </Modal>
    );
};

CGCModal.propTypes = {
    classes: object.isRequired,
    openModal: bool.isRequired,
    closeModal: func.isRequired,
    brandData: shape({
        brand_logo: shape({
            url: string.isRequired,
        }),
        card_back_view_disclaimer: string.isRequired,
        back_card_text: string.isRequired,
        card_size_heigh: number.isRequired,
        card_size_width: number.isRequired,
        back_view_font_size: number.isRequired,

    }).isRequired,
    occasionData: arrayOf(
        shape({
            rank: number.isRequired,
            refrence: arrayOf(
                shape({
                    uid: string.isRequired,
                }),
            ).isRequired,
        }),
    ).isRequired,
    orderItemsId: string.isRequired,
    brandUid: string.isRequired,
    brand: shape({
        domain: string,
    }).isRequired,
    addCGCData: func.isRequired,
    editCard: shape({
        orderItemId: string,
        cgcCard: shape({
            uid: string,
        }),
        cgcMessage: shape({
            style: shape({
                textAlign: string,
                fontFamily: string,
                color: string,
            }),
            text: string,
        }),
    }),
    getCGCKey: func.isRequired,
    productSku: string.isRequired,
    dataDictionary: object,
};

CGCModal.defaultProps = {
    editCard: {},
    dataDictionary: {},
};

const mapStateToProps = (state) => ({
    existingCgcData: getCGCCardData(state),
    dataDictionary: getDataDictionary(state),
});

const mapDispatchToProps = (dispatch) => ({
    addCGCData: bindActionCreators(addCGCDataToApi, dispatch),
    getCGCKey: bindActionCreators(fetchGCIKey, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(CGCModal));
